import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Check, ArrowLeft, Loader2, Mail, Waves, User } from "lucide-react";
import { toast } from "sonner";

const CheckinPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error, need_auth
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  
  // Auth form state
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [debugLink, setDebugLink] = useState(null);

  useEffect(() => {
    if (code) {
      if (user) {
        doCheckin();
      } else {
        setStatus("need_auth");
      }
    }
  }, [code, user]);

  const doCheckin = async () => {
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/checkin/${code}`, {}, { withCredentials: true });
      setResult(res.data);
      setStatus("success");
      toast.success(res.data.message);
    } catch (e) {
      const detail = e.response?.data?.detail || "Erro no check-in";
      setErrorMsg(detail);
      setStatus("error");
    }
  };

  const handleRequestMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Insere o teu email");
      return;
    }
    
    setAuthLoading(true);
    try {
      // Request magic link with redirect back to this check-in page
      const res = await axios.post(`${API}/auth/magic-link`, {
        email: email.trim(),
        name: name.trim() || undefined,
        redirect: `/checkin/${code}`  // Redirect back here after auth
      });
      
      setMagicLinkSent(true);
      if (res.data.debug_link) {
        setDebugLink(res.data.debug_link);
      }
      toast.success("Magic link enviado! Verifica o teu email.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar magic link");
    } finally {
      setAuthLoading(false);
    }
  };

  // Auth required state - inline login form
  if (status === "need_auth") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f0f4f5] to-[#e8eff1] flex items-center justify-center p-4">
        <Card className="bg-white border-0 shadow-lg rounded-2xl max-w-sm w-full" data-testid="checkin-auth-card">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#6697a4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Waves className="h-8 w-8 text-[#6697a4]" />
              </div>
              <h2 className="text-xl font-bold text-[#3d5a63] mb-1">Check-in na Aula</h2>
              <p className="text-sm text-[#94A3B8]">
                Autentica-te para registar a tua presença
              </p>
            </div>

            {!magicLinkSent ? (
              <form onSubmit={handleRequestMagicLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#3d5a63]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="o.teu@email.com"
                      className="pl-10"
                      required
                      data-testid="checkin-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#3d5a63]">Nome (opcional)</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="O teu nome"
                      className="pl-10"
                      data-testid="checkin-name-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#6697a4] hover:bg-[#3d5a63]"
                  disabled={authLoading}
                  data-testid="checkin-auth-submit"
                >
                  {authLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      A enviar...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Enviar Magic Link
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-[#94A3B8]">
                  Vais receber um link no email para fazer login e check-in automático
                </p>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="h-8 w-8 text-[#10B981]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#3d5a63] mb-1">Email enviado!</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Verifica a tua caixa de correio em <strong>{email}</strong>
                  </p>
                  <p className="text-xs text-[#94A3B8] mt-2">
                    Clica no link para fazer login e o check-in será automático
                  </p>
                </div>
                
                {debugLink && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                    <p className="text-xs text-amber-700 mb-2">🔧 Debug (email não configurado):</p>
                    <a 
                      href={debugLink} 
                      className="text-xs text-[#6697a4] underline break-all"
                      data-testid="debug-magic-link"
                    >
                      Clica aqui para simular o magic link
                    </a>
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setDebugLink(null);
                  }}
                  className="w-full mt-4"
                >
                  Usar outro email
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main checkin states
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4f5] to-[#e8eff1] flex items-center justify-center p-4">
      <Card className="bg-white border-0 shadow-lg rounded-2xl max-w-sm w-full" data-testid="checkin-card">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="h-16 w-16 text-[#6697a4] mx-auto animate-spin mb-4" />
              <p className="text-[#3d5a63] font-medium">A fazer check-in...</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-[#10B981] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-xl font-bold text-[#3d5a63] mb-2">Check-in realizado!</h2>
              <p className="text-[#475569] mb-1">{result?.lesson?.title}</p>
              <p className="text-sm text-[#94A3B8] mb-6">{result?.lesson?.date} — {result?.lesson?.start_time}</p>
              <Button onClick={() => navigate("/dashboard")} className="bg-[#6697a4] hover:bg-[#3d5a63] w-full" data-testid="go-dashboard">
                Ir para o Dashboard
              </Button>
            </>
          )}
          {status === "error" && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl text-red-500">!</span>
              </div>
              <h2 className="text-xl font-bold text-[#3d5a63] mb-2">Erro no Check-in</h2>
              <p className="text-[#475569] mb-6">{errorMsg}</p>
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckinPage;
